// Registration form component
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GoogleButton from './GoogleButton.js';
import './AuthForms.css';

export default function RegisterForm({ onRegister, onGoogleSignIn, onSwitchToLogin, loading, error }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};

    if (!email.trim()) {
      errors.email = t('auth.errors.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = t('auth.errors.emailInvalid');
    }

    if (!password) {
      errors.password = t('auth.errors.passwordRequired');
    } else if (password.length < 6) {
      errors.password = t('auth.errors.passwordTooShort');
    }

    if (!confirmPassword) {
      errors.confirmPassword = t('auth.errors.confirmPasswordRequired');
    } else if (password !== confirmPassword) {
      errors.confirmPassword = t('auth.errors.passwordsDoNotMatch');
    }

    if (!displayName.trim()) {
      errors.displayName = t('auth.errors.displayNameRequired');
    } else if (displayName.trim().length < 2) {
      errors.displayName = t('auth.errors.displayNameTooShort');
    } else if (displayName.trim().length > 30) {
      errors.displayName = t('auth.errors.displayNameTooLong');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    await onRegister(email.trim(), password, displayName.trim());
  };

  return (
    <div className="auth-form">
      <h2>{t('auth.createAccount')}</h2>

      {error && (
        <div className="auth-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="displayName">{t('auth.displayName')}</label>
          <input
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={loading}
            className={validationErrors.displayName ? 'error' : ''}
            placeholder={t('auth.placeholders.displayName')}
            maxLength={30}
          />
          {validationErrors.displayName && (
            <span className="field-error">{validationErrors.displayName}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">{t('auth.email')}</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            className={validationErrors.email ? 'error' : ''}
            placeholder={t('auth.placeholders.email')}
          />
          {validationErrors.email && (
            <span className="field-error">{validationErrors.email}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="password">{t('auth.password')}</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            className={validationErrors.password ? 'error' : ''}
            placeholder={t('auth.placeholders.password')}
          />
          {validationErrors.password && (
            <span className="field-error">{validationErrors.password}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            className={validationErrors.confirmPassword ? 'error' : ''}
            placeholder={t('auth.placeholders.confirmPassword')}
          />
          {validationErrors.confirmPassword && (
            <span className="field-error">{validationErrors.confirmPassword}</span>
          )}
        </div>

        <button
          type="submit"
          className="auth-button primary"
          disabled={loading}
        >
          {loading ? t('auth.creatingAccount') : t('auth.createAccount')}
        </button>
      </form>

      {/* OR Separator */}
      <div className="auth-separator">
        <div className="separator-line"></div>
        <span className="separator-text">{t('auth.or')}</span>
        <div className="separator-line"></div>
      </div>

      {/* Google Sign Up */}
      <GoogleButton
        onClick={onGoogleSignIn}
        disabled={loading}
        loading={loading}
      >
        {t('auth.continueWithGoogle')}
      </GoogleButton>

      <div className="auth-switch">
        <p>
          {t('auth.alreadyHaveAccount')}{' '}
          <button
            type="button"
            className="auth-link"
            onClick={onSwitchToLogin}
            disabled={loading}
          >
            {t('auth.signIn')}
          </button>
        </p>
      </div>
    </div>
  );
}
